import React, { useState, useEffect, useRef } from 'react';
import { foundersMessageApi, getMediaUrl } from '../services/api';

const DEFAULT_SETTINGS = {
  badgeText: 'PSYCHMITRA',
  titlePrefix: 'Founderʼs',
  titleAccent: 'Message',
  photoImage: '/assets/images/team/team-details-img-1.jpg',
  photoImage2: '/assets/images/team/team-2-1.jpg',
  photoAlt: 'Pooja Jagyasi - Psychologist & Co-Founder',
  photoAlt2: 'Sneha Nandanwar - Psychologist & Co-Founder',
  photoBadgeNames: 'Pooja Jagyasi & Sneha Nandanwar',
  photoBadgeName1: 'Pooja Jagyasi',
  photoBadgeName2: 'Sneha Nandanwar',
  photoBadgeRole: 'Psychologists & Co-Founders',
  photoBadgeRole1: 'Psychologist & Co-Founder',
  photoBadgeRole2: 'Psychologist & Co-Founder',
  isEnabled: true,
};

const DEFAULT_MESSAGES = [
  {
    _id: 'default-pooja',
    name: 'Pooja Jagyasi',
    role: 'Psychologist | Co-Founder',
    quote: "I've always believed that mental health work is as much about systems as it is about sessions. I've seen how difficult it can be for people to access the right kind of care and how exhausting it can be for practitioners to offer it within rigid, limiting structures.",
    secondaryQuote: "PsychMitra came from a simple intention: to create a space where care feels ethical, accessible, and sustainable for everyone involved. A space where mental health is approached with patience, respect, and humanity, not pressure or compromise.",
    accentColor: '#7c4bb9',
    order: 1,
  },
  {
    _id: 'default-sneha',
    name: 'Sneha Nandanwar',
    role: 'Psychologist | Co-Founder',
    quote: "I believe mental health has always been about understanding humans beyond labels, diagnoses, and timelines. People heal in different ways, and care needs to meet them where they are.",
    secondaryQuote: "PsychMitra was born from the belief that people need choice, safety, and room to explore what support looks like for them. I wanted to help build a space where mental health feels less intimidating, more inclusive, and deeply human for both those seeking care and those offering it.",
    accentColor: '#9072c8',
    order: 2,
  },
];

const FoundersMessageSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [messages, setMessages] = useState(DEFAULT_MESSAGES);
  const sectionRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        const res = await foundersMessageApi.getPublicFoundersMessage();
        if (isMounted && res?.success && res.data) {
          if (res.data.settings) {
            setSettings(res.data.settings);
          }
          if (Array.isArray(res.data.messages) && res.data.messages.length > 0) {
            setMessages(res.data.messages);
          }
        }
      } catch (err) {
        // Fallback already pre-set to DEFAULT_SETTINGS & DEFAULT_MESSAGES
        console.debug('Founder messages loaded default fallback:', err);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  if (settings.isEnabled === false) {
    return null;
  }

  const photoSrc1 = settings.photoImage ? getMediaUrl(settings.photoImage) : (messages[0]?.avatar ? getMediaUrl(messages[0].avatar) : '/assets/images/team/team-details-img-1.jpg');
  const photoSrc2 = settings.photoImage2 ? getMediaUrl(settings.photoImage2) : (messages[1]?.avatar ? getMediaUrl(messages[1].avatar) : '/assets/images/team/team-2-1.jpg');
  const name1 = settings.photoBadgeName1 || messages[0]?.name || 'Pooja Jagyasi';
  const role1 = settings.photoBadgeRole1 || messages[0]?.role || 'Psychologist & Co-Founder';
  const name2 = settings.photoBadgeName2 || messages[1]?.name || 'Sneha Nandanwar';
  const role2 = settings.photoBadgeRole2 || messages[1]?.role || 'Psychologist & Co-Founder';

  return (
    <section className="founders-message-section" ref={sectionRef} id="founders-message">
      {/* Ambient Floating Decorative Orbs */}
      <div className="founders-ambient-orb founders-ambient-orb-1"></div>
      <div className="founders-ambient-orb founders-ambient-orb-2"></div>

      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        {/* Section Header */}
        <div className={`section-title text-center founders-header-anim ${isVisible ? 'founders-active' : ''}`}>
          <span className="founders-header-badge">
            <span className="founders-badge-dot"></span>
            {settings.badgeText || 'PSYCHMITRA'}
          </span>
          <h2 className="section-title__title founders-main-title">
            {settings.titlePrefix || 'Founderʼs'}{' '}
            <span className="founders-title-accent">{settings.titleAccent || 'Message'}</span>
          </h2>
        </div>

        <div className="row align-items-center">
          {/* Left: Dual Founder Photos Column (Slide in from Left) */}
          <div className="col-xl-5 col-lg-5 mb-4 mb-lg-0">
            <div className={`founders-dual-column ${isVisible ? 'photo-active' : ''}`}>
              {/* Co-Founders Header Ribbon */}
              <div className="founders-duo-ribbon">
                <span className="founders-duo-sparkle">✨</span>
                <span className="founders-duo-ribbon-text">{settings.photoBadgeNames || `${name1} & ${name2}`}</span>
              </div>

              {/* Dual Photos Grid */}
              <div className="founders-dual-photo-grid">
                {/* Founder 1 Card - Pooja Jagyasi */}
                <div className="founders-photo-card founders-photo-card--1">
                  <div className="founders-photo-inner">
                    <div className="founders-img-wrapper">
                      <img
                        src={photoSrc1}
                        alt={settings.photoAlt || `${name1} - ${role1}`}
                        className="founders-img"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '/assets/images/team/team-details-img-1.jpg';
                        }}
                      />
                      <div className="founders-img-gradient-overlay"></div>
                    </div>

                    {/* Individual Badge Overlay */}
                    <div className="founders-card-badge">
                      <h5 className="founders-card-name" title={name1}>{name1}</h5>
                      <p className="founders-card-role" title={role1}>{role1}</p>
                    </div>
                  </div>
                </div>

                {/* Founder 2 Card - Sneha Nandanwar */}
                <div className="founders-photo-card founders-photo-card--2">
                  <div className="founders-photo-inner">
                    <div className="founders-img-wrapper">
                      <img
                        src={photoSrc2}
                        alt={settings.photoAlt2 || `${name2} - ${role2}`}
                        className="founders-img"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '/assets/images/team/team-2-1.jpg';
                        }}
                      />
                      <div className="founders-img-gradient-overlay"></div>
                    </div>

                    {/* Individual Badge Overlay */}
                    <div className="founders-card-badge">
                      <h5 className="founders-card-name" title={name2}>{name2}</h5>
                      <p className="founders-card-role" title={role2}>{role2}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Trust Badge */}
              {/* <div className="founders-duo-footer-badge">
                <div className="founders-duo-footer-icon">
                  <i className="fas fa-heart"></i>
                </div>
                <div className="founders-duo-footer-text">
                  <strong>Dedicated to Ethical, Safe & Accessible Care</strong>
                  <span>Compassionate Minds • Evidence-Based Support</span>
                </div>
              </div> */}
            </div>
          </div>

          {/* Right: Founder Messages Column (Slide in from Right with Stagger) */}
          <div className="col-xl-7 col-lg-7">
            <div className="founders-quotes-wrapper">
              {messages.map((item, idx) => {
                const borderAccent = item.accentColor || (idx % 2 === 0 ? '#7c4bb9' : '#9072c8');
                const isFounder2 = (item.name && name2 && item.name.toLowerCase().includes(name2.toLowerCase())) ||
                  (!item.name && idx === 1) ||
                  (item.name && item.name.toLowerCase().includes('sneha')) ||
                  (idx === 1);
                const fallbackAvatar = isFounder2 ? photoSrc2 : photoSrc1;
                const authorAvatar = item.avatar ? getMediaUrl(item.avatar) : fallbackAvatar;

                return (
                  <div
                    key={item._id || idx}
                    className={`founder-quote-box ${isVisible ? 'quote-active' : ''}`}
                    style={{
                      borderLeft: `6px solid ${borderAccent}`,
                      transitionDelay: `${0.15 * (idx + 1)}s`,
                    }}
                  >
                    <div className="founder-quote-shimmer"></div>
                    <div className="founder-quote-header">
                      <div className="founder-quote-author-info">
                        {/* <img
                          src={authorAvatar}
                          alt={item.name}
                          className="founder-quote-avatar"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = isFounder2 ? '/assets/images/team/team-2-1.jpg' : '/assets/images/team/team-details-img-1.jpg';
                          }}
                        /> */}
                        <div>
                          <h4 className="founder-quote-name" style={{ color: '#241242' }}>
                            {item.name}
                          </h4>
                          <span className="founder-quote-title" style={{ color: borderAccent }}>
                            {item.role || 'Psychologist | Co-Founder'}
                          </span>
                        </div>
                      </div>
                      <div
                        className="founder-quote-icon"
                        style={{
                          color: borderAccent,
                          background: `${borderAccent}15`,
                        }}
                      >
                        <i className="fas fa-quote-right"></i>
                      </div>
                    </div>

                    {/* Main Quote / Paragraphs */}
                    {item.quote && (
                      <div className="founder-quote-body">
                        {item.quote
                          .split(/\n+/)
                          .map((para, pIdx) => (
                            <p key={pIdx} className="founder-quote-text" style={{ marginBottom: item.secondaryQuote || pIdx < item.quote.split(/\n+/).length - 1 ? '12px' : '0' }}>
                              {para.trim().startsWith('"') ? para.trim() : `"${para.trim()}"`}
                            </p>
                          ))}
                      </div>
                    )}

                    {/* Secondary Quote / Paragraph if provided */}
                    {item.secondaryQuote && (
                      <div className="founder-quote-body">
                        {item.secondaryQuote
                          .split(/\n+/)
                          .map((para, pIdx) => (
                            <p key={pIdx} className="founder-quote-text" style={{ margin: pIdx === item.secondaryQuote.split(/\n+/).length - 1 ? 0 : '12px' }}>
                              {para.trim().startsWith('"') ? para.trim() : `"${para.trim()}"`}
                            </p>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* =========================================================
           FOUNDER'S MESSAGE ANIMATIONS & INTERACTION STYLES
           ========================================================= */

        .founders-message-section {
          position: relative;
          padding: 95px 0 90px;
          background: linear-gradient(180deg, #fcfaff 0%, #f7f2fc 50%, #fbf8fe 100%);
          overflow: hidden;
        }

        /* Ambient Glowing Background Orbs */
        .founders-ambient-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(85px);
          pointer-events: none;
          z-index: 1;
          opacity: 0.55;
          animation: foundersOrbFloat 14s ease-in-out infinite alternate;
        }

        .founders-ambient-orb-1 {
          top: 10%;
          left: -6%;
          width: 380px;
          height: 380px;
          background: radial-gradient(circle, rgba(124, 75, 185, 0.22) 0%, transparent 70%);
        }

        .founders-ambient-orb-2 {
          bottom: 5%;
          right: -6%;
          width: 420px;
          height: 420px;
          background: radial-gradient(circle, rgba(144, 114, 200, 0.2) 0%, transparent 70%);
          animation-delay: -7s;
        }

        @keyframes foundersOrbFloat {
          0% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-30px) scale(1.1) translateX(20px); }
          100% { transform: translateY(20px) scale(0.95) translateX(-15px); }
        }

        /* Header Animation */
        .founders-header-anim {
          opacity: 0;
          transform: translateY(25px);
          transition: opacity 0.8s ease, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
          margin-bottom: 50px;
        }

        .founders-header-anim.founders-active {
          opacity: 1;
          transform: translateY(0);
        }

        .founders-header-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #f0e9ff;
          color: #7c4bb9;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          padding: 7px 22px;
          border-radius: 30px;
          margin-bottom: 12px;
          box-shadow: 0 4px 15px rgba(124, 75, 185, 0.1);
        }

        .founders-badge-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #7c4bb9;
          box-shadow: 0 0 8px #7c4bb9;
          animation: foundersDotPulse 2s infinite;
        }

        @keyframes foundersDotPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.6; }
        }

        .founders-main-title {
          font-size: clamp(2.2rem, 4vw, 2.75rem);
          font-weight: 800;
          color: #1e1335;
          margin-top: 6px;
          letter-spacing: -0.5px;
        }

        .founders-title-accent {
          background: linear-gradient(135deg, #7c4bb9 0%, #9072c8 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          position: relative;
          display: inline-block;
        }

        /* Left Side: Dual Photo Column & Grid */
        .founders-dual-column {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 16px;
          opacity: 0;
          transform: translateX(-50px) scale(0.96);
          transition: opacity 0.85s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.9s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .founders-dual-column.photo-active {
          opacity: 1;
          transform: translateX(0) scale(1);
        }

        .founders-duo-ribbon {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #ffffff;
          border: 1.5px solid rgba(124, 75, 185, 0.2);
          padding: 6px 16px;
          border-radius: 24px;
          font-size: 13px;
          font-weight: 700;
          color: #7c4bb9;
          align-self: flex-start;
          box-shadow: 0 4px 14px rgba(124, 75, 185, 0.08);
        }

        .founders-duo-sparkle {
          font-size: 14px;
          line-height: 1;
        }

        .founders-dual-photo-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .founders-photo-card {
          position: relative;
        }

        .founders-photo-inner {
          position: relative;
          border-radius: 22px;
          overflow: hidden;
          box-shadow: 0 16px 36px rgba(124, 75, 185, 0.14), 0 4px 10px rgba(0, 0, 0, 0.04);
          border: 3.5px solid #ffffff;
          background: #ffffff;
          transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1),
                      box-shadow 0.4s ease;
        }

        .founders-photo-inner:hover {
          transform: translateY(-7px);
          box-shadow: 0 22px 48px rgba(124, 75, 185, 0.22), 0 0 22px rgba(168, 85, 247, 0.16);
        }

        .founders-img-wrapper {
          position: relative;
          width: 100%;
          height: 360px;
          overflow: hidden;
          background: #ede5f8;
        }

        .founders-img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          object-position: center 20%;
          transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .founders-photo-inner:hover .founders-img {
          transform: scale(1.05);
        }

        .founders-img-gradient-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(30, 19, 53, 0) 45%, rgba(30, 19, 53, 0.75) 100%);
          pointer-events: none;
        }

        /* Floating Overlay Badge on each Founder Card */
        .founders-card-badge {
          position: absolute;
          bottom: 12px;
          left: 10px;
          right: 10px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          padding: 10px 12px;
          border-radius: 15px;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
          border: 1.5px solid rgba(124, 75, 185, 0.22);
          transition: transform 0.3s ease, background-color 0.3s ease;
        }

        .founders-photo-inner:hover .founders-card-badge {
          transform: translateY(-3px);
          background: #ffffff;
        }

        .founders-card-name {
          margin: 0;
          font-size: 14.5px;
          font-weight: 800;
          color: #241242;
          line-height: 1.25;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .founders-card-role {
          margin: 3px 0 0;
          font-size: 11px;
          color: #7c4bb9;
          font-weight: 700;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Duo Footer Badge */
        .founders-duo-footer-badge {
          display: flex;
          align-items: center;
          gap: 12px;
          background: #ffffff;
          border: 1.5px solid #ede5f8;
          padding: 12px 18px;
          border-radius: 18px;
          box-shadow: 0 8px 22px rgba(124, 75, 185, 0.08);
        }

        .founders-duo-footer-icon {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background: linear-gradient(135deg, #7c4bb9 0%, #9072c8 100%);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          box-shadow: 0 4px 12px rgba(124, 75, 185, 0.3);
          flex-shrink: 0;
        }

        .founders-duo-footer-text strong {
          display: block;
          font-size: 13.5px;
          color: #241242;
          font-weight: 700;
          line-height: 1.3;
        }

        .founders-duo-footer-text span {
          display: block;
          font-size: 11.5px;
          color: #7c4bb9;
          font-weight: 600;
        }

        /* Right Side: Quotes Wrapper */
        .founders-quotes-wrapper {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        /* Quote Boxes with Slide from Right */
        .founder-quote-box {
          position: relative;
          background-color: #ffffff;
          border-radius: 24px;
          padding: 30px 32px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
          border: 1.5px solid #ede5f8;
          overflow: hidden;
          opacity: 0;
          transform: translateX(70px) scale(0.96);
          transition: opacity 0.85s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.85s cubic-bezier(0.34, 1.56, 0.64, 1),
                      border-color 0.35s ease,
                      box-shadow 0.35s ease;
        }

        .quote-pooja {
          border-left: 6px solid #7c4bb9;
          transition-delay: 0.15s;
        }

        .quote-sneha {
          border-left: 6px solid #9072c8;
          transition-delay: 0.3s;
        }

        .founder-quote-box.quote-active {
          opacity: 1;
          transform: translateX(0) scale(1);
        }

        /* Quote Hover Lift & Glow */
        .founder-quote-box.quote-active:hover {
          transform: translateY(-6px) translateX(4px);
          border-color: #c4b5fd;
          box-shadow: 0 20px 45px rgba(124, 75, 185, 0.14);
        }

        /* Light Shimmer Streak on Hover */
        .founder-quote-shimmer {
          position: absolute;
          top: 0;
          left: -120%;
          width: 60%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.7),
            transparent
          );
          transform: skewX(-25deg);
          transition: left 0.8s ease;
          pointer-events: none;
          z-index: 5;
        }

        .founder-quote-box:hover .founder-quote-shimmer {
          left: 150%;
        }

        .founder-quote-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
          gap: 12px;
        }

        .founder-quote-author-info {
          display: flex;
          align-items: center;
          gap: 14px;
          min-width: 0;
        }

        .founder-quote-avatar {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          object-fit: cover;
          object-position: center 20%;
          border: 2.5px solid #ffffff;
          box-shadow: 0 4px 14px rgba(124, 75, 185, 0.22);
          flex-shrink: 0;
          transition: transform 0.3s ease;
        }

        .founder-quote-box:hover .founder-quote-avatar {
          transform: scale(1.08);
        }

        .founder-quote-name {
          margin: 0;
          font-size: 21px;
          font-weight: 800;
          color: #241242;
          transition: color 0.3s ease;
          line-height: 1.25;
        }

        .founder-quote-box:hover .founder-quote-name {
          color: #7c4bb9;
        }

        .founder-quote-title {
          font-size: 13.5px;
          color: #7c4bb9;
          font-weight: 700;
          display: block;
          margin-top: 2px;
        }

        .founder-quote-icon {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          background: #f5effe;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #7c4bb9;
          font-size: 18px;
          flex-shrink: 0;
          transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
                      background 0.3s ease,
                      color 0.3s ease;
        }

        .founder-quote-box:hover .founder-quote-icon {
          transform: rotate(15deg) scale(1.15);
          background: #7c4bb9;
          color: #ffffff;
        }

        .founder-quote-text {
          font-size: 15px;
          line-height: 1.72;
          color: #555160;
          margin-bottom: 12px;
        }

        /* Responsive Styles */
        @media (max-width: 767px) {
          .founders-message-section {
            padding: 65px 0 55px;
          }
          .founder-quote-box {
            padding: 24px 20px;
          }
          .founders-img-wrapper {
            height: 280px;
          }
        }

        @media (max-width: 576px) {
          .founders-dual-photo-grid {
            gap: 10px;
          }
          .founders-img-wrapper {
            height: 220px;
          }
          .founders-photo-inner {
            border-radius: 16px;
            border-width: 2.5px;
          }
          .founders-card-badge {
            bottom: 8px;
            left: 6px;
            right: 6px;
            padding: 7px 8px;
            border-radius: 12px;
          }
          .founders-card-name {
            font-size: 12px;
          }
          .founders-card-role {
            font-size: 9.5px;
          }
          .founders-duo-ribbon {
            padding: 5px 12px;
            font-size: 12px;
          }
          .founders-duo-footer-badge {
            padding: 10px 14px;
            gap: 10px;
          }
          .founders-duo-footer-icon {
            width: 32px;
            height: 32px;
            font-size: 12px;
          }
          .founders-duo-footer-text strong {
            font-size: 12px;
          }
          .founders-duo-footer-text span {
            font-size: 10.5px;
          }
          .founder-quote-avatar {
            width: 42px;
            height: 42px;
          }
          .founder-quote-name {
            font-size: 18px;
          }
          .founder-quote-title {
            font-size: 12px;
          }
          .founder-quote-icon {
            width: 36px;
            height: 36px;
            font-size: 15px;
          }
        }
      `}</style>
    </section>
  );
};

export default FoundersMessageSection;
